import { Divider } from "@mui/material";
import { useState } from "react";

import Modal from "../../../../../global/Modal";
import useModifyProviderDetails from "../../hooks/useModifyProviderDetails";
import { useProviderDetailStore } from "../../store/provider-details/providerDetailStore";
import AddressField from "../fields/address-field/AddressField";
import CommPreferencesField from "../fields/communication-preference-field/CommPreferencesField";
import CustomTextField from "../fields/CustomTextField";
import ProviderTypeSearchField from "../fields/provider-type-field/ProviderTypeSearchField";
import RatingField from "../fields/rating-field/RatingField";
import SingleSelectDropdown from "../fields/single-select-dropdown-field/SingleSelectDropdown";
import ActionButtons from "../provider-details/ActionButtons";
import AdditionalEmailsEditView from "../provider-details/AdditionalEmailsEditView";
import AdditionalPhonesEditView from "../provider-details/AdditionalPhonesEditView";
import ModalField from "../provider-details/ModalField";

export default function ProviderDetailEditModal({ openModal, handleCloseModal }) {
  const { modifiedData, disableCancelButton, disableSaveButton, booleanOptionsData, activeStates } = useProviderDetailStore();
  const {
    handleFieldOnChangeValue,
    handleOnClickSaveButton,
    handleOnClickCancelButton,
    handleAdditionalEmailOnChange,
    handleAdditionalEmailDelete,
    handleAdditionalPhoneDelete,
    handleProviderTypeChange,
  } = useModifyProviderDetails();

  const providerName = modifiedData?.name ?? "";
  const providerTypes = modifiedData?.providerTypes ?? [];
  const address = modifiedData?.address ?? null;
  const website = modifiedData?.homepage ?? null;
  const activeState = modifiedData?.activeState ?? null;
  const commPreference = modifiedData?.communicationPreference ?? null;
  const phone = modifiedData?.phone ?? null;
  const mainEmail = modifiedData?.email ?? null;
  const summary = modifiedData?.notes ?? null;
  const emails = modifiedData?.emails ?? {};
  const contactInfos = modifiedData?.contactInfos ?? {};
  const inNetwork = modifiedData?.inNetwork;
  const acceptsFunding = modifiedData?.acceptsFunding;
  const receivingClients = modifiedData?.receivingClients;
  const rating = modifiedData?.rating;
  const [addressFieldValue, setAddressFieldValue] = useState(address);

  return (
    <Modal open={openModal} onClose={handleCloseModal} maxWidth="md">
      <div className="flex flex-col gap-2 p-6">
        <div className="flex w-full flex-row gap-4">
          <ModalField
            label={"Provider Name"}
            renderValue={
              <CustomTextField
                fieldValue={providerName}
                fieldName={"name"}
                onChange={handleFieldOnChangeValue}
              />
            }
          />
        </div>
        <div className="flex w-full flex-row gap-4">
          <ModalField
            label={"Provider Rating"}
            renderValue={
              <RatingField
                fieldValue={rating}
                fieldName={"rating"}
                onChange={handleFieldOnChangeValue}
              />
            }
        />
        </div>
        <div className="flex w-full flex-row gap-4 py-4">
          <ModalField
            label={"Provider Type"}
            renderValue={
              <ProviderTypeSearchField
                fieldName={"providerTypes"}
                fieldValue={providerTypes}
                onChange={handleProviderTypeChange}
              />
            }
          />
        </div>
        <div className="flex w-full flex-row gap-4">
          <ModalField
            label={"Website URL"}
            renderValue={
              <CustomTextField
                fieldValue={website}
                fieldName={"homepage"}
                onChange={handleFieldOnChangeValue}
              />
            }
          />
          <ModalField
            label={"Active State"}
            renderValue={
              <SingleSelectDropdown
                fieldValue={activeState}
                fieldName={"activeState"}
                onChange={handleFieldOnChangeValue}
                data={activeStates}
              />
            }
          />
        </div>
        <div className="flex w-full flex-row gap-4">
          <ModalField
            label={""}
            renderValue={
              <AddressField
                defaultEditingMode={true}
                fieldValue={addressFieldValue}
                fieldName={"address"}
                onChange={(fieldName, val) => {
                  setAddressFieldValue(val);
                  handleFieldOnChangeValue(fieldName, val);
                }}
              />
            }
          />
        </div>
        <div className="flex w-full flex-row gap-4">
          <ModalField
            label="Communication Preference"
            renderValue={
              <CommPreferencesField
                fieldValue={commPreference}
                fieldName={"communicationPreference"}
                onChange={handleFieldOnChangeValue}
              />
            }
          />
          <ModalField
            label={"Phone Number"}
            renderValue={
              <CustomTextField
                fieldValue={phone}
                fieldName={"phone"}
                onChange={handleFieldOnChangeValue}
              />
            }
          />
          <ModalField
            label={"Main Email"}
            renderValue={
              <CustomTextField
                fieldValue={mainEmail}
                fieldName={"email"}
                onChange={handleFieldOnChangeValue}
              />
            }
          />
        </div>
        <div className="flex w-full flex-row gap-4">
          <ModalField
            label={"In Network"}
            renderValue={
              <SingleSelectDropdown
                fieldValue={inNetwork}
                fieldName={"inNetwork"}
                onChange={handleFieldOnChangeValue}
                data={booleanOptionsData}
              />
            }
          />
          <ModalField
            label={"Receiving Clients"}
            renderValue={
              <SingleSelectDropdown
                fieldValue={receivingClients}
                fieldName={"receivingClients"}
                onChange={handleFieldOnChangeValue}
                data={booleanOptionsData}
              />
            }
          />
          <ModalField
            label={"Accepts Funding"}
            renderValue={
              <SingleSelectDropdown
                fieldValue={acceptsFunding}
                fieldName={"acceptsFunding"}
                onChange={handleFieldOnChangeValue}
                data={booleanOptionsData}
              />
            }
          />
        </div>
        <div className="flex flex-col gap-4 py-2">
          <Divider />
        </div>
        <div>
          <AdditionalEmailsEditView
            fieldName={"emails"}
            fieldValue={emails}
            onChange={handleAdditionalEmailOnChange}
            onDelete={handleAdditionalEmailDelete}
          />
        </div>
        <div className="flex flex-col gap-4 py-2">
          <Divider />
        </div>
        <div>
          <AdditionalPhonesEditView
            fieldName={"contactInfos"}
            fieldValue={contactInfos}
            onChange={handleAdditionalEmailOnChange}
            onDelete={handleAdditionalPhoneDelete}
          />
        </div>
        <div className="flex flex-col gap-4 py-2">
          <Divider />
        </div>
        <div>
          <ModalField
            label={"Provider Summary"}
            renderValue={
              <CustomTextField
                fieldValue={summary}
                multiline={true}
                noClearIcon={true}
                fieldName={"notes"}
                onChange={handleFieldOnChangeValue}
              />
            }
          />
        </div>
        <ActionButtons
          disableCancel={disableCancelButton}
          disableSave={disableSaveButton}
          handleOnClickSaveButton={handleOnClickSaveButton}
          handleOnClickCancelBtn={handleOnClickCancelButton}
        />
      </div>
    </Modal>
  );
}
